import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

/**
 * Sortable Controller
 *
 * Wraps SortableJS to make a list of elements drag-reorderable, persisting
 * the new order via PATCH to the configured URL.
 *
 * Each child needs `data-sortable-id="<record-id>"`. The controller reads
 * those ids in current DOM order and PATCHes them as `{ ids: [...] }`.
 *
 * Cross-list moves (e.g., services dragged between categories) are handled
 * by setting `data-sortable-group-value` to the same string on both lists.
 * `onAdd` fires on the destination list — its `category_id` is sent in the
 * PATCH so the backend can move the service.
 *
 * Usage:
 *   <tbody data-controller="sortable"
 *          data-sortable-url-value="/operations/services/reorder"
 *          data-sortable-group-value="services"
 *          data-sortable-category-id-value="42">
 *     <tr data-sortable-id="7" ...>
 *     <tr data-sortable-id="9" ...>
 *   </tbody>
 *
 * Survives Stimulus reconnects: SortableJS in `forceFallback` mode physically
 * moves the dragged DOM node mid-drag, which Stimulus interprets as the
 * controller element being re-attached. We stash the Sortable instance on
 * the element itself so reconnect reuses it instead of destroying the
 * in-flight drag state.
 */
export default class extends Controller {
  static values = {
    url: String,
    group: String,
    categoryId: { type: String, default: "" },
    handle: { type: String, default: "" }
  };

  connect() {
    if (this.element._sortableInstance) {
      this.sortable = this.element._sortableInstance;
      return;
    }

    const opts = {
      group: this.groupValue,
      animation: 150,
      ghostClass: "opacity-50",
      // Use SortableJS's own pointer-event-based drag rather than HTML5
      // drag-and-drop. HTML5 drag breaks on <tbody><tr> (table layout
      // collapses during drag) and onEnd never fires.
      forceFallback: true,
      fallbackOnBody: true,
      onEnd: this.persist.bind(this),
      onAdd: this.persist.bind(this)
    };
    // Restrict drag origin to a child selector when configured (e.g.,
    // categories use `.drag-handle` so the section's clickable header
    // doesn't accidentally trigger drags). When omitted, dragging can
    // start anywhere on the item — used for service rows.
    if (this.handleValue) opts.handle = this.handleValue;

    this.sortable = Sortable.create(this.element, opts);
    this.element._sortableInstance = this.sortable;
  }

  disconnect() {
    // Only tear down if the element has actually left the DOM. Stimulus
    // also calls disconnect when an element is just being moved (e.g.,
    // SortableJS re-arranging during a drag), and destroying mid-drag
    // breaks onEnd. The next connect() will reuse the cached instance.
    if (!this.element.isConnected) {
      this.element._sortableInstance?.destroy();
      delete this.element._sortableInstance;
      this.sortable = null;
    }
  }

  persist() {
    const ids = Array.from(this.element.children)
      .map((el) => el.dataset.sortableId)
      .filter((id) => id !== undefined && id !== "");

    if (ids.length === 0) return;

    const payload = { ids };
    if (this.groupValue === "services") {
      payload.category_id = this.categoryIdValue || null;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify(payload)
    })
      .then((response) => {
        if (!response.ok) {
          console.error("Sortable persist failed:", response.status);
        }
      })
      .catch((error) => {
        console.error("Sortable persist failed:", error);
      });
  }
}
